// Pure, framework-free helpers for the Start-a-Company public wizard.
// Extracted so the compliance-sensitive logic (tax-id masking, tax-id field
// gating, ownership validation) is unit-testable without rendering Stripe.

import type { OwnerInput, TaxIdType } from '../../api/company-registration-public'

// The SSN/ITIN input is shown ONLY for U.S. identifier types. A foreign / none
// applicant is NEVER asked for an identifier.
export function taxIdRequiresIdentifier(type: TaxIdType | null | undefined): boolean {
  return type === 'ssn' || type === 'itin'
}

// Mask a masked-read last4 (from the fetched view) to `•••••1234`. Returns
// an em-dash when there is nothing to show.
export function maskTaxIdLast4(last4: string | null | undefined): string {
  if (!last4) return '—'
  return `•••••${last4}`
}

// Mask a just-entered plaintext SSN/ITIN client-side (we never send it back and
// never render it in full). Keeps only the last 4 visible: `•••••1234`.
export function maskTaxIdValue(value: string | null | undefined): string {
  if (!value) return '—'
  const digits = value.replace(/\D/g, '')
  if (digits.length < 4) return '•••••'
  return `•••••${digits.slice(-4)}`
}

// Sum of provided ownership percentages (missing/blank treated as 0).
export function ownershipSum(owners: Pick<OwnerInput, 'ownership_percentage'>[]): number {
  return owners.reduce((acc, o) => {
    const pct =
      typeof o.ownership_percentage === 'number' && Number.isFinite(o.ownership_percentage)
        ? o.ownership_percentage
        : 0
    return acc + pct
  }, 0)
}

// True only when at least one owner supplied a percentage AND the total > 100.
// (An all-blank list is valid — percentages are optional at intake.)
export function isOwnershipOverfull(
  owners: Pick<OwnerInput, 'ownership_percentage'>[],
): boolean {
  const anySupplied = owners.some(
    (o) =>
      typeof o.ownership_percentage === 'number' &&
      Number.isFinite(o.ownership_percentage),
  )
  if (!anySupplied) return false
  // Small epsilon so 33.33 * 3 style rounding does not false-trip.
  return ownershipSum(owners) > 100.0001
}

// number_of_members derives from the count of NAMED owner rows (blank rows are
// ignored so it matches the owners payload, which is filtered to non-empty
// full_name). Stays >= 1 and remains user-overridable.
export function deriveNumberOfMembers(owners: { full_name?: string }[]): number {
  const named = owners.filter((o) => (o.full_name ?? '').trim() !== '').length
  return Math.max(1, named)
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function isValidEmail(email: string): boolean {
  return EMAIL_RE.test(email.trim())
}
